import { useEffect, useRef, useState } from 'react'
import { streamAssistant } from '@/lib/assistantStream'
import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { Badge, Button, EmptyState, ErrorNote, Input, Panel, SectionHeading } from '@/components/ui'
import { cn } from '@/lib/cn'

interface Turn {
  role: 'user' | 'assistant'
  content: string
  sources?: { name: string; type: string }[]
}

const SUGGESTIONS = [
  'What does the Restrained condition do?',
  'How does Sneak Attack actually work?',
  'Can a level 1 paladin cast Cure Wounds?',
  'What happens on a natural 1 on a death save?',
]

export function AssistantTab({ campaignId }: { campaignId: string }) {
  const { data: status, loading: checking } = useAsync(
    () => api.assistantStatus(campaignId), [campaignId],
  )
  const [turns, setTurns] = useState<Turn[]>([])
  const [question, setQuestion] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abort = useRef<AbortController | null>(null)
  const bottom = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns, streaming])

  useEffect(() => () => abort.current?.abort(), [])

  async function ask(text: string) {
    const trimmed = text.trim()
    if (!trimmed || streaming) return

    setError(null)
    setQuestion('')
    const history: Turn[] = [...turns, { role: 'user', content: trimmed }]
    setTurns([...history, { role: 'assistant', content: '' }])
    setStreaming(true)

    abort.current = new AbortController()
    try {
      await streamAssistant(
        campaignId,
        history.map(({ role, content }) => ({ role, content })),
        {
          onSources: (sources) => setTurns((current) => withLast(current, (last) => ({ ...last, sources }))),
          onText: (chunk) => setTurns((current) => withLast(current, (last) => ({ ...last, content: last.content + chunk }))),
          onError: (message) => setError(message),
        },
        abort.current.signal,
      )
    } catch (err) {
      if ((err as Error).name !== 'AbortError') setError('Lost the connection to the assistant.')
    } finally {
      setStreaming(false)
      // Drop the placeholder if nothing ever arrived.
      setTurns((current) => {
        const last = current[current.length - 1]
        return last && last.role === 'assistant' && !last.content ? current.slice(0, -1) : current
      })
    }
  }

  if (checking) return <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>

  if (!status?.configured) {
    return (
      <EmptyState
        title="The rules assistant is not set up yet"
        description={
          'It needs an Anthropic API key, which only you can add. Put ANTHROPIC_API_KEY in .dev.vars ' +
          'to try it locally, and run "wrangler pages secret put ANTHROPIC_API_KEY" for the deployed site. ' +
          'Everything else in the app works without it.'
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <SectionHeading
        action={turns.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => { setTurns([]); setError(null) }}>
            New conversation
          </Button>
        )}
      >
        Rules assistant
      </SectionHeading>

      {turns.length === 0 ? (
        <Panel className="space-y-4 p-5">
          <p className="text-sm leading-relaxed text-ink-300">
            Ask a rules question. Answers are grounded in this campaign's own rules database first,
            and the assistant will tell you when something falls outside it rather than guessing.
            Only you can see this.
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => void ask(suggestion)}
                className="rounded-full border border-ink-600 px-3 py-1.5 text-[13px] text-ink-400 transition-colors hover:border-gold-600 hover:text-gold-400"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </Panel>
      ) : (
        <div className="space-y-3">
          {turns.map((turn, index) => (
            <div key={index} className={cn('flex', turn.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[85%] rounded-[--radius-card] border p-3.5',
                  turn.role === 'user'
                    ? 'border-gold-600/40 bg-gold-900/25'
                    : 'border-ink-700/70 bg-ink-850/70',
                )}
              >
                {turn.role === 'assistant' && turn.sources && turn.sources.length > 0 && (
                  <div className="mb-2.5 flex flex-wrap gap-1.5 border-b border-ink-700/60 pb-2.5">
                    <span className="text-[10px] uppercase tracking-wider text-ink-500">Looked at</span>
                    {turn.sources.slice(0, 6).map((source) => (
                      <Badge key={`${source.type}-${source.name}`}>{source.name}</Badge>
                    ))}
                  </div>
                )}
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-200">
                  {turn.content}
                  {streaming && index === turns.length - 1 && (
                    <span aria-hidden className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-gold-500 align-middle" />
                  )}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottom} />
        </div>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      <form
        onSubmit={(event) => { event.preventDefault(); void ask(question) }}
        className="sticky bottom-4 flex gap-2"
      >
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask a rules question…"
          aria-label="Ask the rules assistant"
          disabled={streaming}
          className="h-11"
        />
        {streaming ? (
          <Button type="button" variant="secondary" onClick={() => abort.current?.abort()}>Stop</Button>
        ) : (
          <Button type="submit" variant="primary" disabled={!question.trim()}>Ask</Button>
        )}
      </form>
    </div>
  )
}

function withLast(turns: Turn[], update: (last: Turn) => Turn): Turn[] {
  if (turns.length === 0) return turns
  return [...turns.slice(0, -1), update(turns[turns.length - 1]!)]
}
